/**
 * Pure helpers around the `getRideQuote` callable.
 *
 * Kept out of services/firebase.js (which can't be imported in a plain node
 * test) so the two things below are unit-tested:
 *  - which fields of the server's answer the app keeps, and
 *  - how a "too many requests" refusal is retried.
 */
import { bookingErrorKey } from './rideErrors.js';

/**
 * The quote the app works with, from the callable's raw `data`.
 *
 * Every field the server adds to the quote must be listed here, or it never
 * reaches the confirm screen: `pickupInfo` (nearest auto, ETA, tip chips) and
 * `waiting` (waiting-charge terms) were once dropped by an inline copy of this.
 * Both are absent on an older server or when the best-effort lookup failed, so
 * they stay `null` rather than `undefined`.
 */
export const parseRideQuote = (data) => {
  const d = data || {};
  return {
    fare: Number(d.fare),
    distanceKm: Number(d.distanceKm),
    durationMin: Number(d.durationMin),
    fareSnapshot: d.fareSnapshot || null,
    pickupInfo: d.pickupInfo || null,
    waiting: d.waiting || null,
  };
};

/**
 * The server allows one quote per rider per second. Two lookups close together
 * (a pickup/drop swap right after the first fare, a double tap) make the second
 * one fail with "too many requests", which is a one-second wait, not a problem.
 * The refusal is not recorded server-side, so a retry just over a second later
 * goes through.
 */
export const QUOTE_RETRY_DELAY_MS = 1100;
export const QUOTE_MAX_RETRIES = 2;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Run `fn` (a quote request) again, after a pause, while the server answers
 * "too many requests". Any other error, running out of retries, or `shouldStop()`
 * turning true (the rider has since changed the route) rethrows the error
 * unchanged for the caller to handle as before.
 */
export const retryWhenRateLimited = async (fn, {
  shouldStop = () => false,
  delayMs = QUOTE_RETRY_DELAY_MS,
  retries = QUOTE_MAX_RETRIES,
  wait = sleep,
} = {}) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= retries || bookingErrorKey(err) !== 'book.errTooMany' || shouldStop()) throw err;
      await wait(delayMs);
      if (shouldStop()) throw err;
    }
  }
};
